// ============================================================================
// GAME CONFIGURATION
// Central place for all game parameters and tuning values
// ============================================================================

const CONFIG = {
    // ========================================
    // CANVAS & WORLD
    // ========================================
    canvasWidth: 1280,  // Wider standard web game size
    canvasHeight: 720,  // 16:9 aspect ratio
    worldWidth: 1300,   // Reduced by 6 columns (300 pixels) for sandbox testing
    worldHeight: 1200,
    tileSize: 50,
    
    // Debug mode settings
    debugAggressionMultiplier: 1,  // Aggression accumulation multiplier in debug mode (for faster testing)

    // ========================================
    // PHYSICS (Global)
    // ========================================
    environmentFriction: 0.70,       // How quickly objects slow down (0-1, lower = more friction) - doubled from 0.92
    wallBounceEnergyLoss: 0.9,       // Energy retained after wall bounce (0-1)
    
    // Substepping for fast-moving objects (prevents tunneling)
    physicsSubstepMaxDistance: 5,   // Maximum safe distance (pixels) per substep to prevent tunneling
    physicsSubstepMaxCount: 5000,       // Maximum number of substeps per frame (performance cap)
    
    // Physics solver iterations (higher = more accurate but slower)
    physicsPositionIterations: 40,   // Position constraint solver iterations (default: 6)
    physicsVelocityIterations: 40,   // Velocity constraint solver iterations (default: 4)

    // Throwable spawn/throw settings
    throwableMaxAggression: 150,         // Maximum aggression value used for spawn/throw (prevents tunneling at extreme values)
    throwableSpawnSpeedMultiplier: 0.7,  // Speed multiplier when spawned from breaking (reduced from 1.0)
    throwableThrowSpeedMultiplier: 0.7,  // Speed multiplier when thrown (reduced from 0.2 for door, 0.2 for mirror)
    throwableSpawnVariance: 1.0,         // Random variance for spawn speed (0.5x to 0.5x + this value, e.g. 1.0 = 0.5x to 1.5x)
    throwableRotationSpeedMin: 0.02,      // Minimum rotation speed (rad/sec) - random between min and max
    throwableRotationSpeedMax: 0.05,      // Maximum rotation speed (rad/sec) - random between min and max
    throwableAngularDamping: 0.95,    // Angular damping multiplier (0-1, higher = more damping) - applied manually each frame
    throwableFrictionAir: 0.15,        // Air friction for throwables (affects both linear and angular velocity) - higher = more realistic

    // ========================================
    // PLAYER
    // ========================================
    playerSize: 20,
    playerColor: '#4CAF50',     // Changed from blue to green (calm/happy state)
    aggressionRate: 15.0,        // Aggression accumulation rate per second (multiplied by debugAggressionMultiplier in debug mode)

    // Run mechanic
    doubleClickThreshold: 0.3,   // Max time between clicks/keys for double-click (seconds)

    // ========================================
    // INTERACTION
    // ========================================
    interactionRadius: 60,

    // ========================================
    // PICKUP & THROW SYSTEM
    // ========================================
    pickupRadius: 40,            // Distance to pick up objects
    throwChargeDelay: 0.5,        // Delay before throw charging starts (seconds) - allows quick pickup without charging
    throwChargeTimeMax: 2.0,      // Maximum charge time (seconds) - same as aggressiveMax
    throwForceBase: 200,          // Base throw force (minimum charge)
    throwForceMax: 1200,          // Maximum throw force (full charge)

    // ========================================
    // AGGRESSION SYSTEM
    // ========================================
    // Time thresholds (in seconds)
    interactionBaselineTime: 1.0,  // Time before damage applies (allows normal use without damage) - aggression still accumulates for visual feedback
    gentleMax: 0.5,          // Gentle interaction
    moderateMax: 1.0,        // Moderate aggression
    aggressiveMax: 2.0,      // High aggression
    maxConMaxAggression: 100, // Maximum possible aggression (gameplay limit, separate from throwableMaxAggression)

    // ========================================
    // DAMAGE SYSTEM
    // ========================================
    cupboardDamageThresholds: [5, 15, 30],  // Visual damage stages for Cupboard
    chestDamageThresholds: [5, 15, 30],     // Visual damage stages for Chest
    wardrobeDamageThresholds: [5, 15, 30],  // Visual damage stages for Wardrobe
    breakThreshold: 80,              // Cupboard breaks at this damage
    wardrobeBreakThreshold: 100,    // Wardrobe breaks at this damage
    chestBreakThreshold: 50,         // Chest breaks at this damage
    doorBreakThreshold: 50,          // Door breaks at this damage
    maxTilt: 10,                     // Maximum tilt angle in degrees

    // ========================================
    // VISUAL FEEDBACK (by aggression level)
    // ========================================
    // Screen shake
    shakeAmplitudes: [0, 2, 5, 10],      // [gentle, low, moderate, aggressive]
    shakeDurations: [0, 0.2, 0.4, 0.6],  // in seconds

    // Particles
    particleCounts: [0, 15, 35, 80],

    // Animation
    doorOpenSpeed: [0.5, 0.3, 0.2, 0.15],  // Duration in seconds
    doorOvershoot: [0, 5, 15, 30],         // Degrees of overshoot

    // ========================================
    // CAMERA EFFECTS
    // ========================================
    extremeZoom: 1.1,
    extremeZoomDuration: 0.3,  // seconds

    // ========================================
    // ALARM CLOCK
    // ========================================
    alarmClockBreakThreshold: 20,        // Alarm clock breaks at this damage (vs 100 for cupboard)
    alarmClockInterval: 3,              // Alarm turns on every 30 seconds
    alarmClockDamageThresholds: [5, 10, 15],  // Visual damage stages for alarm clock

    // ========================================
    // ANGER COLOR PROGRESSION SYSTEM
    // ========================================
    // Color scheme: Green → Yellow → Orange → Red → Dark Red/Purple → Ultra-dark Purple
    // This progression follows the heat/temperature metaphor (cool → hot → extreme)
    // and is culturally intuitive (traffic lights, warning systems)
    //
    // Anger levels are 0-100, divided into 6 stages with equal spacing:
    angerColorStages: [
        {
            min: 0,
            max: 20,
            name: 'Calm',
            color: '#4CAF50',      // Green - Neutral/happy state
            description: 'Calm and content'
        },
        {
            min: 20,
            max: 40,
            name: 'Irritated',
            color: '#FFEB3B',      // Yellow - Slight irritation
            description: 'Beginning to feel annoyed'
        },
        {
            min: 40,
            max: 60,
            name: 'Angry',
            color: '#FF9800',      // Orange - Moderate anger
            description: 'Moderate level of anger'
        },
        {
            min: 60,
            max: 80,
            name: 'Furious',
            color: '#F44336',      // Red - High anger
            description: 'High level of anger'
        },
        {
            min: 80,
            max: 90,
            name: 'Extreme Rage',
            color: '#6A1B9A',      // Dark Red/Purple - Extreme rage
            description: 'Extreme level of rage'
        },
        {
            min: 90,
            max: 100,
            name: 'Ultra Extreme',
            color: '#1A0033',      // Ultra-dark purple/almost black - Ultra extreme rage
            description: 'Maximum possible rage level'
        }
    ],

    // Emotion/Anger system
    angerEnabled: false,  // Enable/disable anger system (wall hits, color changes, rage actions)
    angerDecayRate: 0.5,         // How fast anger decreases per second
    angerThreshold: 30,          // Anger level to trigger visible reactions (deprecated, using color stages now)
    angerColorTransitionSpeed: 5, // How fast color changes
    punchDuration: 0.3,          // Duration of punch animation

    // Anger triggers
    angerFromWallHit: 25,        // Anger gained from hitting wall during run
    angerFromIdleTimeout: 10,    // Anger from being idle too long (future)

    // ========================================
    // COLORS
    // ========================================
    colors: {
        cupboard: '#A0522D',
        woodParticle: '#8B4513',
        dustParticle: '#D3D3D3',
        leafParticle: '#228B22', // Green leaf color (will be mixed with light/dark greens)
        leafParticleLight: '#90EE90', // Light green
        leafParticleDark: '#006400', // Dark green
        alarmClock: '#E0E0E0',
        bed: '#8B4513',
        sideTable: '#654321'
    },

    // ========================================
    // WIND SYSTEM
    // ========================================
    windChangeInterval: 60,  // Wind direction changes every 60 seconds (1 minute)
    windStrength: 0.08,      // Wind strength (affects leaf drift) - very slow, subtle effect

    // ========================================
    // OBJECTS (Future: Different object types)
    // ========================================
    // Example structure for future objects:
    // objects: {
    //     cupboard: {
    //         breakThreshold: 100,
    //         friction: 0.92
    //     },
    //     chair: {
    //         breakThreshold: 50,
    //         friction: 0.85
    //     }
    // }
};

export default CONFIG;
